// Жесты закрытия окон на телефоне (мобильный аудит, P3-02).
//
// Свайп вниз закрывает только нижние листы без черновика (ModalLayer, swipe по умолчанию),
// свайп влево закрывает меню, свайп вправо по левой половине экрана открывает его. Чат и
// форма подачи в СНС — через Sheet, жест им не подключается: он конфликтует с прокруткой, а
// введённый текст можно потерять. Жест срабатывает, только если он начался не в поле ввода,
// идёт по одной оси и содержимое не прокручено (иначе это прокрутка, а не закрытие).

import { hasOpenLayer } from "./layers";

const DISTANCE = 80;
const AXIS_RATIO = 1.5;
const FIELD = "input, textarea, select, [contenteditable], [data-no-swipe]";

type Dir = "down" | "left" | "right";

function scrolledInside(from: Element | null, stop: Element, dir: Dir): boolean {
  for (let el = from; el && el !== stop.parentElement; el = el.parentElement) {
    if (!(el instanceof HTMLElement)) continue;
    const style = getComputedStyle(el);
    if (dir === "down" && /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight && el.scrollTop > 0) return true;
    if (dir !== "down" && /(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth) return true;
  }
  return false;
}

/**
 * Подписывает el на жест; вернёт функцию отписки. Для "right" жест засчитывается, только если
 * начался в левой половине экрана и не поверх открытого окна (иначе это прокрутка таблицы или
 * жест внутри листа).
 */
export function onSwipe(el: HTMLElement, dir: Dir, close: () => void): () => void {
  let x0 = 0, y0 = 0, tracking = false;
  const start = (e: TouchEvent) => {
    tracking = false;
    if (e.touches.length !== 1) return;
    const t = e.target as Element | null;
    if (t?.closest(FIELD) || scrolledInside(t, el, dir)) return;
    if (dir === "right" && (hasOpenLayer() || e.touches[0].clientX > window.innerWidth / 2)) return;
    x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; tracking = true;
  };
  // Срабатываем уже в touchmove: в Android WebView (Telegram, MAX) браузер нередко забирает жест
  // и шлёт touchcancel вместо touchend — тогда «по отпусканию» жест бы пропал.
  const check = (x: number, y: number) => {
    const dx = x - x0, dy = y - y0;
    const hit =
      (dir === "down" && dy > DISTANCE && dy > Math.abs(dx) * AXIS_RATIO) ||
      (dir === "right" && dx > DISTANCE && dx > Math.abs(dy) * AXIS_RATIO) ||
      (dir === "left" && -dx > DISTANCE && -dx > Math.abs(dy) * AXIS_RATIO);
    if (hit) { tracking = false; close(); }
  };
  const move = (e: TouchEvent) => { if (tracking) check(e.touches[0].clientX, e.touches[0].clientY); };
  const end = (e: TouchEvent) => {
    if (!tracking) return;
    check(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    tracking = false;
  };
  const cancel = () => { tracking = false; };
  el.addEventListener("touchstart", start, { passive: true });
  el.addEventListener("touchmove", move, { passive: true });
  el.addEventListener("touchend", end, { passive: true });
  el.addEventListener("touchcancel", cancel, { passive: true });
  return () => {
    el.removeEventListener("touchstart", start);
    el.removeEventListener("touchmove", move);
    el.removeEventListener("touchend", end);
    el.removeEventListener("touchcancel", cancel);
  };
}
